import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES } from '@/data/destinations'
import type { ExportType } from '@/types'

const TIMES = [
  ['8:30am', '11:00am', '2:30pm'],
  ['9:00am', '1:00pm', '4:00pm'],
  ['10:00am', '12:30pm', '5:00pm'],
]

const QR_PATTERN = [1,0,1,1,0,0,1,0,1,1,0,1,1,0,1,0,0,1,1,0,1,1,0,0,1]

function QRCode() {
  return (
    <div className="w-10 h-10 bg-ink rounded-sm mx-auto mt-2.5 p-0.5 grid grid-cols-5 gap-px">
      {QR_PATTERN.map((v, i) => (
        <div key={i} className="rounded-[1px]" style={{ background: v ? '#FFFAF4' : '#1A1A2E' }} />
      ))}
    </div>
  )
}

export function JourneyCardScreen() {
  const { selectedDestination, dnaKey, setScreen, resetJourney } = useAppStore()
  const [activeExport, setActiveExport] = useState<ExportType>('card')
  const [phoneMode, setPhoneMode] = useState<'sms' | 'whatsapp'>('sms')
  const [phoneSent, setPhoneSent] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [shareCopied, setShareCopied] = useState(false)

  const dest = selectedDestination
  const profile = DNA_PROFILES[dnaKey]
  if (!dest || !profile) { setScreen('journey'); return null }

  const handleExport = (type: ExportType) => {
    if (type === 'itinerary') {
      setScreen('itinerary')
      return
    }
    setActiveExport(type)
    setPhoneSent(false)
  }

  const shareLine = `Our family journey to ${dest.name} — planned with Yonder by Imaginarium. See it: yonder.imaginarium.club`
  const handleCopyShare = () => {
    try {
      navigator.clipboard?.writeText(shareLine)
    } catch {
      // clipboard unavailable — non-fatal, still show confirmation
    }
    setShareCopied(true)
    setTimeout(() => setShareCopied(false), 2000)
  }

  return (
    <div className="yonder-world min-h-screen flex flex-col">
      <Atmosphere density="low" />
      <Topbar
        centerContent={<span className="text-white/70 font-bold">{dest.name} · family journey card</span>}
        backTo="journey"
        backLabel="← Itinerary"
      />

      <div className="relative z-10 flex-1 px-4 py-4">
        <div className="grid sm:grid-cols-[1fr_220px] gap-4 items-start max-w-3xl mx-auto">

          {/* Journey Card Visual */}
          <div className="rounded-3xl overflow-hidden yonder-cream-card shadow-cream-card">
            {/* Hero */}
            <div
              className="h-28 flex items-center justify-center text-5xl relative"
              style={{ background: dest.gradient }}
            >
              {dest.emoji}
              <div className="absolute top-2 left-3 text-[9px] font-black tracking-widest text-white/70 uppercase">
                {dest.flag}
              </div>
              <div className="absolute bottom-2 left-3 font-black text-white text-xl tracking-tight">
                {dest.name}
              </div>
              <div className="absolute bottom-2 right-3 text-[10px] font-bold text-white/70">
                family journey
              </div>
            </div>

            {/* Card body */}
            <div className="p-3">
              {/* DNA row */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🧬</span>
                <div>
                  <div className="text-[13px] font-black text-ink">{profile.name}</div>
                  <div className="text-[10px] font-semibold text-ink-3">Your family travel DNA</div>
                </div>
              </div>

              {/* Days */}
              <div className="flex flex-col gap-1.5">
                {dest.stops.slice(0, 3).map((stop, i) => (
                  <div key={i} className="border-t border-[#F0EBE3] pt-1.5">
                    <div className="text-[9px] font-black tracking-wider text-ink-3 uppercase mb-0.5">Day {i + 1}</div>
                    <div className="text-[11px] font-black text-ink">{stop.name}</div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {TIMES[i].map((t, ti) => (
                        <span key={ti} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#F0EBE3] text-ink-3">
                          ⏱ {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Savings */}
              <div className="mt-2.5 p-2 bg-gradient-to-r from-[#E8FFF6] to-[#D0FFE9] border border-grass rounded-xl flex items-center gap-2 text-[11px] font-bold text-grass-light">
                <span>💰</span>
                <span>{dest.saving} partner savings via Yonder</span>
              </div>

              <QRCode />

              <div className="text-center mt-2 text-[9px] font-bold text-ink-3 tracking-wider border-t border-[#F0EBE3] pt-2">
                Yonder by Imaginarium · yonder.imaginarium.club
              </div>
            </div>
          </div>

          {/* Export panel */}
          <div>
            <div className="text-[13px] font-black text-white mb-3 flex items-center gap-1.5">
              🗺 Your Journey Card
            </div>

            {/* Export options — standardised 4-option pattern (order/icons/copy match all strands) */}
            {(
              [
                { type: 'card' as ExportType, icon: '✦', title: 'Journey Card', desc: 'Your DNA, destinations & partner savings' },
                { type: 'itinerary' as ExportType, icon: '☰', title: 'Full Itinerary', desc: 'Complete day-by-day plan' },
                { type: 'phone' as ExportType, icon: '📱', title: 'Send to Phone', desc: 'Get it via SMS or WhatsApp' },
                { type: 'share' as ExportType, icon: '🔗', title: 'Share Link', desc: 'Share with travel companions' },
              ] as const
            ).map((item) => (
              <div key={item.type}>
                <button
                  onClick={() => handleExport(item.type)}
                  className={`w-full text-left border-2 rounded-2xl p-3 mb-2 transition-all ${
                    activeExport === item.type
                      ? 'border-sun bg-white/15 shadow-glow-sun'
                      : 'border-white/10 bg-white/5 hover:border-sun-light/60 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sun-pale text-sm">{item.icon}</span>
                    <span className="text-[12px] font-black text-white">{item.title}</span>
                  </div>
                  <div className="text-[10px] font-semibold text-white/60 leading-snug">{item.desc}</div>
                </button>

                {/* Card focus state — confirms the journey card preview to the left is live */}
                {item.type === 'card' && activeExport === 'card' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-2 px-1"
                  >
                    <div className="flex items-center gap-2 text-[11px] font-bold text-sun-pale bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                      <span>👈</span>
                      <span>This is your live Journey Card — printable, shareable, ready to go.</span>
                    </div>
                  </motion.div>
                )}

                {/* Share focus state — copyable share line */}
                {item.type === 'share' && activeExport === 'share' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-2 px-1"
                  >
                    <div className="bg-white/10 border-2 border-white/15 rounded-xl px-3 py-2 text-[11px] font-semibold text-white/80 mb-2 break-words">
                      {shareLine}
                    </div>
                    <button
                      onClick={handleCopyShare}
                      className="w-full bg-gradient-to-r from-sun to-sun-light text-white font-black text-[12px] py-2 rounded-xl shadow-glow-sun"
                    >
                      {shareCopied ? '✅ Copied to clipboard' : '🔗 Copy share link'}
                    </button>
                  </motion.div>
                )}

                {/* Phone input panel — SMS/WhatsApp toggle + numeric keypad + Send flow */}
                {item.type === 'phone' && activeExport === 'phone' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-2 px-1"
                  >
                    {!phoneSent ? (
                      <>
                        <div className="flex gap-1.5 mb-2">
                          {(['sms', 'whatsapp'] as const).map((m) => (
                            <button
                              key={m}
                              onClick={() => setPhoneMode(m)}
                              className={`flex-1 py-1.5 text-[11px] font-bold border-2 rounded-xl transition-all ${
                                phoneMode === m
                                  ? 'border-sun text-sun-pale bg-sun/10'
                                  : 'border-white/10 text-white/60 bg-white/5'
                              }`}
                            >
                              {m === 'sms' ? '📱 SMS' : '💬 WhatsApp'}
                            </button>
                          ))}
                        </div>
                        <select className="w-full bg-white/10 border-2 border-transparent rounded-xl text-[11px] font-semibold text-white p-2 mb-2 outline-none focus:border-sun">
                          <option>GB +44 United Kingdom</option>
                          <option>US +1 United States</option>
                          <option>AU +61 Australia</option>
                          <option>IE +353 Ireland</option>
                          <option>ZA +27 South Africa</option>
                        </select>
                        <input
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="Your mobile number…"
                          inputMode="numeric"
                          className="w-full bg-white/10 border-2 border-transparent rounded-xl text-[12px] font-semibold text-white placeholder-white/40 px-3 py-2 mb-2 outline-none focus:border-sun focus:bg-white/15 transition-all"
                        />
                        {/* Numeric keypad */}
                        <div className="grid grid-cols-3 gap-1.5 mb-2">
                          {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((k, ki) => (
                            <button
                              key={ki}
                              type="button"
                              disabled={!k}
                              onClick={() => {
                                if (k === '⌫') setPhoneNumber((p) => p.slice(0, -1))
                                else if (k) setPhoneNumber((p) => p + k)
                              }}
                              className={`py-1.5 rounded-lg text-[12px] font-bold transition-all ${
                                k
                                  ? 'bg-white/10 text-white hover:bg-white/20 active:scale-95'
                                  : 'opacity-0 pointer-events-none'
                              }`}
                            >
                              {k}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setPhoneSent(true)}
                          className="w-full bg-gradient-to-r from-sun to-sun-light text-white font-black text-[12px] py-2 rounded-xl shadow-glow-sun"
                        >
                          Send now 🚀
                        </button>
                      </>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-2"
                      >
                        <div className="text-2xl mb-1">✅</div>
                        <div className="text-[11px] font-bold text-grass-light">
                          Sent ✓ — check your phone, your family adventure awaits.
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}

            <button
              onClick={() => window.print()}
              className="w-full bg-gradient-to-r from-sun to-sun-light text-white font-black text-[13px] py-3 rounded-2xl shadow-glow-sun mt-1 mb-2"
            >
              🖨 Print Journey Card
            </button>
            <button
              onClick={resetJourney}
              className="w-full bg-white/10 text-white/70 font-bold text-[11px] py-2.5 rounded-2xl hover:bg-white/15 transition-colors"
            >
              + Start a new journey
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
